import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeftRight, Loader2, Link2, Trash2, Building2 } from "lucide-react";

const LINKS_EVENT = "linked-accounts-changed";
export const notifyLinksChanged = () => window.dispatchEvent(new Event(LINKS_EVENT));

export function useLinkedAccounts(refreshKey = 0) {
  const { token, user, settings } = useAuth();
  const [links, setLinks] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(LINKS_EVENT, h);
    return () => window.removeEventListener(LINKS_EVENT, h);
  }, []);
  useEffect(() => {
    if (!token || user?.ruolo !== "titolare" || !settings?.multi_azienda_enabled) { setLinks([]); return; }
    axios.get(`${API}/auth/linked-accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLinks(r.data.links || []))
      .catch(() => setLinks([]));
  }, [token, user, settings?.multi_azienda_enabled, refreshKey, tick]);
  return links;
}

// Pulsanti "Passa a: <azienda>" per la sidebar/menu (solo titolare con account collegati)
export function SwitchAccountButtons({ onSwitched }) {
  const { switchAccount } = useAuth();
  const navigate = useNavigate();
  const links = useLinkedAccounts();
  const [busy, setBusy] = useState(null);
  if (!links.length) return null;

  const go = async (l) => {
    setBusy(l.azienda_id);
    try {
      await switchAccount(l.azienda_id);
      toast.success(`Ora sei in: ${l.nome_azienda}`);
      onSwitched?.();
      navigate("/");
    } catch (e) { toast.error(e.response?.data?.detail || "Cambio azienda non riuscito"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mt-3 space-y-1" data-testid="switch-account-block">
      <p className="px-2 text-[11px] uppercase tracking-wide text-slate-400">Passa a</p>
      {links.map((l) => (
        <Button key={l.azienda_id} variant="ghost" onClick={() => go(l)} disabled={busy === l.azienda_id}
          className="w-full text-slate-700 hover:text-slate-900 hover:bg-slate-100 justify-start" data-testid={`switch-account-${l.azienda_id}`}>
          {busy === l.azienda_id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />}
          <span className="truncate">{l.nome_azienda}</span>
        </Button>
      ))}
    </div>
  );
}

// Card Impostazioni: collega/scollega altre aziende
export function LinkedAccountsCard() {
  const { token } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const links = useLinkedAccounts(refreshKey);
  const [form, setForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await axios.post(`${API}/auth/linked-accounts`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      setForm({ email: "", password: "" });
      setRefreshKey((k) => k + 1);
      notifyLinksChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Collegamento non riuscito"); }
    finally { setSaving(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Scollegare ${l.nome_azienda}?`)) return;
    try {
      await axios.delete(`${API}/auth/linked-accounts/${l.azienda_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Collegamento rimosso");
      setRefreshKey((k) => k + 1);
      notifyLinksChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  return (
    <Card data-testid="linked-accounts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-slate-700" /> Altre aziende collegate</CardTitle>
        <CardDescription>
          Gestisci più aziende? Collega qui l'altro account titolare: nel menu comparirà "Passa a" e cambierai azienda con un clic, senza uscire e rientrare. I dati delle aziende restano separati.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {links.length > 0 && (
          <ul className="divide-y rounded-lg border" data-testid="linked-accounts-list">
            {links.map((l) => (
              <li key={l.azienda_id} className="flex items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{l.nome_azienda}</p>
                    <p className="text-xs text-slate-500 truncate">{l.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(l)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`unlink-${l.azienda_id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
          <div>
            <Label>Email dell'altro account titolare</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="titolare@altra-azienda.it" data-testid="link-email-input" />
          </div>
          <div>
            <Label>Password dell'altro account</Label>
            <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="link-password-input" />
          </div>
          <Button type="submit" disabled={saving} className="btn-theme" data-testid="link-account-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}Collega
          </Button>
        </form>
        <p className="text-xs text-slate-500">La password serve solo per verificare che l'altro account sia tuo: non viene salvata.</p>
      </CardContent>
    </Card>
  );
}
